import {
  Letter,
  FingerConfig,
  LetterSequence,
  TrainingContent,
  FingerType,
  WordTrainingContent
} from '@/types';
import {
  getKeysForFingers,
  getFingerForKey,
  getAllActiveFingers
} from '@/data/fingerKeyMapping';
import {
  generateWordTrainingContent
} from '@/services/wordTrainingService';
import { getHandFromFingersForWordTraining } from '@/utils/fingerCombinationMapper';

// Training mode interfaces
export interface TrainingMode {
  hands: 'left' | 'right' | 'both';
  complexity: 1 | 3 | 5 | 'words';
}

export interface EnhancedFingerConfig extends FingerConfig {
  trainingMode: TrainingMode;
}

// Configuration for training generation
const TRAINING_CONFIG = {
  ROUND_DURATION_MS: 120000, // 2 minutes
  ESTIMATED_LETTERS_PER_SECOND: 3, // Conservative estimate for beginners
  BUFFER_MULTIPLIER: 1.5, // Generate 50% more content than estimated need
  MIN_SEQUENCE_LENGTH: 1,
  MAX_SEQUENCE_LENGTH: 5,
  SPACE_FREQUENCY: 0.15, // 15% chance for space character
};

/**
 * Generates random letter sequences based on finger configuration
 */
export class TrainingGenerator {
  private availableKeys: string[] = [];
  private fingerConfig: FingerConfig;
  private sequences: LetterSequence[] = [];

  constructor(fingerConfig: FingerConfig) {
    console.log('🚀 TrainingGenerator initialized (Day 8 Fix Applied)');
    this.fingerConfig = fingerConfig;
    this.initializeAvailableKeys();
  }

  private initializeAvailableKeys(): void {
    const activeFingers = getAllActiveFingers(this.getFingerConfig());

    console.log('🔧 TrainingGenerator initializing with fingers:', activeFingers);

    if (activeFingers.length === 0) {
      console.error('❌ No active fingers in configuration:', this.fingerConfig);
      throw new Error('No active fingers configured for training');
    }

    this.availableKeys = getKeysForFingers(activeFingers);

    if (this.availableKeys.length === 0) {
      console.error('❌ No keys available for fingers:', activeFingers);
      throw new Error('No available keys for training generation');
    }

    // Handle space character specially
    if (this.availableKeys.includes('SPACE')) {
      const index = this.availableKeys.indexOf('SPACE');
      this.availableKeys[index] = '_'; // Visual representation of space
    }

    console.log('✅ Training generator initialized with keys:', this.availableKeys);
    console.log('🎯 Complexity settings:', this.getFingerConfig().letterComplexity);
    console.log('🗺 Keys for each finger:');
    activeFingers.forEach(finger => {
      const keys = getKeysForFingers([finger]);
      console.log(`  ${finger}: ${keys.join(', ')}`);
    });
  }

  // ✅ PHASE 3: Add getter for finger config to allow access
  public getFingerConfig(): FingerConfig {
    return this.fingerConfig;
  }

  /**
   * ENHANCED: Generates a complete training session content with complexity debugging
   */
  public generateTrainingContent(): TrainingContent {
    console.log('📦 Generating training content with complexity:', this.getFingerConfig().letterComplexity);

    const estimatedLettersNeeded = Math.ceil(
      (TRAINING_CONFIG.ROUND_DURATION_MS / 1000) *
      TRAINING_CONFIG.ESTIMATED_LETTERS_PER_SECOND *
      TRAINING_CONFIG.BUFFER_MULTIPLIER
    );

    this.sequences = [];
    let totalLetters = 0;
    let complexityTestCount = 0;

    while (totalLetters < estimatedLettersNeeded && complexityTestCount < 50) {
      const sequence = this.generateSequence();
      this.sequences.push(sequence);
      totalLetters += sequence.letters.length;
      complexityTestCount++;

      // Debug first few sequences to verify complexity
      if (complexityTestCount <= 10) {
        console.log(`🔍 Sequence ${complexityTestCount}: "${sequence.letters.join('')}" (length: ${sequence.complexity})`);
      }
    }

    // ENHANCED: Show complexity distribution analysis
    const complexityDistribution: Record<number, number> = {};
    this.sequences.forEach(seq => {
      complexityDistribution[seq.complexity] = (complexityDistribution[seq.complexity] || 0) + 1;
    });

    console.log(`🎆 Generated ${this.sequences.length} sequences with ${totalLetters} total letters`);
    console.log('📊 COMPLEXITY ANALYSIS:');
    console.log('  Expected range:', this.getFingerConfig().letterComplexity);
    console.log('  Actual distribution:', complexityDistribution);

    // Verify complexity is working
    const hasVariety = Object.keys(complexityDistribution).length > 1;
    const maxObserved = Math.max(...Object.keys(complexityDistribution).map(Number));
    console.log(`✅ Complexity variety: ${hasVariety ? 'YES' : 'NO (PROBLEM!)'}, Max observed: ${maxObserved}`);

    return {
      sequences: this.sequences,
      totalDuration: TRAINING_CONFIG.ROUND_DURATION_MS,
      fingerHints: this.generateFingerHints()
    };
  }

  /**
   * FIXED: Generates a single letter sequence based on complexity configuration
   */
  private generateSequence(): LetterSequence {
    const { min, max } = this.getFingerConfig().letterComplexity;

    // FIXED: Ensure we respect the complexity range properly
    if (min > max) {
      console.error('❌ Invalid complexity range:', { min, max });
      throw new Error(`Invalid complexity range: min (${min}) > max (${max})`);
    }

    const sequenceLength = this.randomBetween(min, max);

    console.log(`🎲 Generating sequence of length ${sequenceLength} (range: ${min}-${max})`);

    const letters: string[] = [];
    const fingerSequence: FingerType[] = [];

    for (let i = 0; i < sequenceLength; i++) {
      let selectedKey: string;
      let selectedFinger: FingerType;

      // Decide if we should include space
      if (sequenceLength === 1 && this.availableKeys.includes('_')) {
        // For single-letter training, treat space as just another regular key
        selectedKey = this.selectRandomKey(this.availableKeys);
        selectedFinger = getFingerForKey(selectedKey === '_' ? 'SPACE' : selectedKey)!;
      } else if (sequenceLength > 1 && this.shouldIncludeSpace() && this.availableKeys.includes('_')) {
        // For longer sequences, sprinkle space randomly
        selectedKey = '_';
        selectedFinger = this.getFingerForSpace();
      } else {
        // Select random key excluding space
        const nonSpaceKeys = this.availableKeys.filter(key => key !== '_');

        if (nonSpaceKeys.length === 0) {
          // If only space is available (e.g. Thumb-only training), use space
          if (this.availableKeys.includes('_')) {
            selectedKey = '_';
            selectedFinger = this.getFingerForSpace();
          } else {
            console.error('❌ No keys available from:', this.availableKeys);
            throw new Error('No keys available for training');
          }
        } else {
          selectedKey = this.selectRandomKey(nonSpaceKeys);
          selectedFinger = getFingerForKey(selectedKey === '_' ? 'SPACE' : selectedKey)!;
        }

        if (!selectedFinger) {
          console.error('❌ No finger found for key:', selectedKey);
          throw new Error(`No finger mapping found for key: ${selectedKey}`);
        }
      }

      letters.push(selectedKey);
      fingerSequence.push(selectedFinger);
    }

    console.log(`✅ Generated sequence: "${letters.join('')}" (length: ${letters.length})`);

    return {
      letters,
      fingerSequence,
      complexity: sequenceLength
    };
  }

  /**
   * COMPLETELY REWRITTEN: Converts training content to Letter sequences for multi-letter display
   */
  public generateLetterSequences(maxComplexity: number): Letter[][] {
    console.log(`🎯 Starting sequence generation with maxComplexity: ${maxComplexity}`);

    const content = this.generateTrainingContent();
    const letterSequences: Letter[][] = [];

    let globalIndex = 0;
    content.sequences.forEach((sequence, _seqIndex) => {
      // Create letter objects for this sequence
      const letters: Letter[] = [];

      sequence.letters.forEach((char, localIndex) => {
        letters.push({
          id: `letter-${globalIndex}`,
          char: char,
          status: 'neutral',
          expectedFinger: sequence.fingerSequence[localIndex],
          timestamp: Date.now()
        });
        globalIndex++;
      });

      // Split the sequence based on maxComplexity (how many letters to show at once)
      for (let i = 0; i < letters.length; i += maxComplexity) {
        const chunk = letters.slice(i, i + maxComplexity);
        letterSequences.push(chunk);
      }
    });

    console.log(`✅ Generated ${letterSequences.length} display sequences`);
    console.log('🔍 First 5 sequences:');
    letterSequences.slice(0, 5).forEach((seq, i) => {
      console.log(`  Sequence ${i + 1}: "${seq.map(l => l.char).join('')}" (${seq.length} letters)`);
    });

    if (letterSequences.length === 0) {
      console.error('❌ No letter sequences generated!');
      throw new Error('Sequence generation failed: No sequences generated');
    }

    return letterSequences;
  }

  /**
  * 🔧 ENHANCED: Optimized sequence generation with better debugging
  */
  public generateMoreSequences(maxComplexity: number, additionalCount: number = 20): Letter[][] {
    console.log(`🔄 Base TrainingGenerator: Generating ${additionalCount} additional sequences with maxComplexity: ${maxComplexity}`);
    console.log(`🔄 Available keys: ${this.availableKeys}`);
    console.log(`🔄 Complexity range: ${this.fingerConfig.letterComplexity.min}-${this.fingerConfig.letterComplexity.max}`);

    const newSequences: Letter[][] = [];
    const timestamp = performance.now(); // More precise timing

    // 🔧 PERFORMANCE: Batch generation for efficiency
    try {
      if (this.availableKeys.length === 0) {
        console.error('❌ No available keys for sequence generation!');
        return [];
      }

      for (let i = 0; i < additionalCount; i++) {
        const sequence = this.generateSequence();

        // Debug the generated sequence
        if (i < 3) { // Log first 3 sequences
          console.log(`🔍 Generated sequence ${i + 1}: "${sequence.letters.join('')}" (complexity: ${sequence.complexity})`);
        }

        const letters: Letter[] = [];

        sequence.letters.forEach((char, localIndex) => {
          letters.push({
            id: `letter-${timestamp}-${i}-${localIndex}`,
            char: char,
            status: 'neutral',
            expectedFinger: sequence.fingerSequence[localIndex],
            timestamp: Date.now()
          });
        });

        // Split the sequence based on maxComplexity
        for (let j = 0; j < letters.length; j += maxComplexity) {
          const chunk = letters.slice(j, j + maxComplexity);
          newSequences.push(chunk);
        }
      }

      console.log(`✅ Generated ${newSequences.length} additional display sequences`);

      // Verify the sequences are diverse
      const uniqueChars = new Set();
      newSequences.slice(0, 10).forEach(seq => {
        seq.forEach(letter => uniqueChars.add(letter.char));
      });
      console.log(`🌈 Character diversity in first 10 sequences: ${Array.from(uniqueChars).join(', ')}`);

      return newSequences;
    } catch (error) {
      console.error('❌ Error generating additional sequences:', error);
      console.error('❌ Available keys at error:', this.availableKeys);
      console.error('❌ Finger config at error:', this.fingerConfig);
      return []; // Return empty array instead of failing
    }
  }

  /**
   * BACKWARD COMPATIBILITY: Legacy method that flattens sequences
   */
  public generateLetters(): Letter[] {
    console.log('⚠️ Using legacy generateLetters method - consider upgrading to generateLetterSequences');
    const sequences = this.generateLetterSequences(this.fingerConfig.letterComplexity.max);
    return sequences.flat();
  }

  /**
   * Generates finger hints for the entire training session
   */
  private generateFingerHints() {
    // This will be expanded in Phase 2 implementation
    return [];
  }

  /**
   * Determines if space should be included in current position
   */
  private shouldIncludeSpace(): boolean {
    return Math.random() < TRAINING_CONFIG.SPACE_FREQUENCY;
  }

  /**
   * Gets the appropriate finger for space character
   */
  private getFingerForSpace(): FingerType {
    const activeFingers = getAllActiveFingers(this.getFingerConfig());

    // Prefer thumbs for space
    if (activeFingers.includes('leftThumb')) return 'leftThumb';
    if (activeFingers.includes('rightThumb')) return 'rightThumb';

    // Fallback to any active finger
    return activeFingers[0];
  }

  /**
   * Selects a random key from available keys with some intelligence
   */
  private selectRandomKey(keys: string[]): string {
    if (keys.length === 0) {
      throw new Error('No available keys for training generation');
    }

    // For now, simple random selection
    // Future enhancement: Add weighted selection based on difficulty
    return keys[Math.floor(Math.random() * keys.length)];
  }

  /**
   * ENHANCED: Generates random number between min and max (inclusive) with detailed debugging
   */
  private randomBetween(min: number, max: number): number {
    if (min > max) {
      console.error('❌ Invalid range for randomBetween:', { min, max });
      throw new Error(`Invalid range: min (${min}) > max (${max})`);
    }

    if (min === max) {
      console.log(`🎲 randomBetween(${min}, ${max}) = ${min} (same min/max)`);
      return min;
    }

    const result = Math.floor(Math.random() * (max - min + 1)) + min;

    // Enhanced debugging for complexity issues
    if (Math.random() < 0.1) { // Log 10% of calls to avoid spam
      console.log(`🎲 randomBetween(${min}, ${max}) = ${result}`);
    }

    return result;
  }

  /**
   * Updates finger configuration and regenerates available keys
   */
  public updateFingerConfig(newConfig: FingerConfig): void {
    this.fingerConfig = newConfig;
    this.initializeAvailableKeys();
  }

  /**
   * Gets statistics about the generated content
   */
  public getGenerationStats() {
    const totalLetters = this.sequences.reduce(
      (sum, seq) => sum + seq.letters.length,
      0
    );

    const averageComplexity = this.sequences.reduce(
      (sum, seq) => sum + seq.complexity,
      0
    ) / this.sequences.length;

    return {
      totalSequences: this.sequences.length,
      totalLetters,
      averageComplexity,
      availableKeys: this.availableKeys,
      estimatedDuration: TRAINING_CONFIG.ROUND_DURATION_MS
    };
  }
}

/**
 * 🔧 PHASE 1 FIX: Enhanced training generator with memory management
 * NO DOUBLE FILTERING - respects the fingerConfig as-is
 */
export class EnhancedTrainingGenerator extends TrainingGenerator {
  private trainingMode: TrainingMode;
  private sequenceBuffer: Letter[][] = [];
  private readonly BUFFER_SIZE = 50;
  private readonly MAX_BUFFER_SIZE = 100;
  private generationInProgress = false;

  constructor(fingerConfig: FingerConfig, trainingMode: TrainingMode) {
    // IMPORTANT: Use the fingerConfig as-is, don't filter again
    // GameScreen has already done the proper filtering
    console.log('EnhancedTrainingGenerator received config:', fingerConfig);
    console.log('TrainingMode:', trainingMode);

    super(fingerConfig); // Pass the config directly without modification
    this.trainingMode = trainingMode;

    // 🔧 PERFORMANCE: Pre-generate initial buffer
    this.fillBuffer();
  }

  /**
   * Override generateLetterSequences to handle both word and letter modes
   * Returns Letter[][] for compatibility with base class
   */
  public generateLetterSequences(maxComplexity: number): Letter[][] {
    if (this.trainingMode.complexity === 'words') {
      console.warn('⚠️ Word mode requires async operation, using fallback letter generation');
      console.log('📝 [INVESTIGATION] Letter mode fallback for synchronous call');
      return super.generateLetterSequences(maxComplexity);
    }

    // For letter modes, use synchronous parent generation
    console.log('📝 [INVESTIGATION] Letter mode detected - using synchronous letter generation');
    return super.generateLetterSequences(maxComplexity);
  }

  /**
   * Async version for word-based generation with manual selection
   */
  public async generateLetterSequencesAsync(maxComplexity: number, manualSelection?: any): Promise<Letter[][]> {
    if (this.trainingMode.complexity === 'words') {
      console.log('🗺️ [INVESTIGATION] Word mode detected - using async word-based generation with manual selection');
      return await this.generateWordBasedSequences(maxComplexity, manualSelection);
    }

    // For letter modes, use synchronous parent generation
    console.log('📝 [INVESTIGATION] Letter mode detected - using synchronous letter generation');
    return super.generateLetterSequences(maxComplexity);
  }

  /**
   * Generate word-based letter sequences using real words from database - ENHANCED LOGGING
   */
  public async generateWordBasedSequences(maxComplexity: number, manualSelection?: any): Promise<Letter[][]> {
    console.log('🗺️ [INVESTIGATION] Generating word-based sequences with maxComplexity:', maxComplexity);
    console.log('🗺️ [INVESTIGATION] Training mode:', this.trainingMode);
    console.log('🗺️ [INVESTIGATION] Current generator type: EnhancedTrainingGenerator');
    console.log('🆕 [MANUAL SELECTION] Received manual selection:', manualSelection);

    try {
      // Check for manual selection first (specific words/phrases from admin)
      if (manualSelection && manualSelection.mode === 'manual' && manualSelection.manual) {
        console.log('🎯 [WORD REPETITION] Manual selection detected - using word repetition logic');
        return await this.generateManualWordSequences(maxComplexity, manualSelection.manual);
      }

      // Get active fingers for this configuration
      const activeFingers = getAllActiveFingers(this.getFingerConfig());
      console.log('🔍 Active fingers for word generation:', activeFingers);

      // Determine hand for word training
      const hand = this.trainingMode.hands === 'both' ?
        getHandFromFingersForWordTraining(activeFingers) : this.trainingMode.hands;

      console.log('🔍 Using hand for word training:', hand);
      console.log('🔍 Training mode hands setting:', this.trainingMode.hands);

      // 🔥 SIMPLIFIED: No consonant detection - just try to get word content
      console.log('📞 Calling generateWordTrainingContent with:', { activeFingers, hand, manualSelection });
      const wordContent = await generateWordTrainingContent(activeFingers, hand, manualSelection);

      console.log('🔍 Word content received:', {
        isEmpty: wordContent?.isEmpty,
        totalItems: wordContent?.totalItems,
        wordsCount: wordContent?.words?.length,
        phrasesCount: wordContent?.phrases?.length,
        message: wordContent?.message,
        fingerCombination: wordContent?.fingerCombination,
        handUsed: wordContent?.hand
      });

      // 🔥 SIMPLIFIED: Handle empty combinations gracefully
      if (!wordContent || wordContent.isEmpty || wordContent.totalItems === 0) {
        console.log('⚠️ [INVESTIGATION] No real word content available - generating pseudo-words instead');
        return this.generatePseudoWordSequences(maxComplexity, activeFingers);
      }

      console.log('✅ Word content is valid, converting to letter sequences');
      const sequences = this.convertWordsToLetterSequences(wordContent, maxComplexity);
      console.log(`✅ Generated ${sequences.length} word-based sequences`);

      if (sequences.length === 0) {
        console.log('⚠️ No sequences generated from word content - generating pseudo-words');
        return this.generatePseudoWordSequences(maxComplexity, activeFingers);
      }

      return sequences;

    } catch (error) {
      console.error('❌ Error generating word-based sequences:', error);
      // 🔥 FALLBACK: Use pseudo-word generation instead of empty array
      return this.generatePseudoWordSequences(maxComplexity, getAllActiveFingers(this.getFingerConfig()));
    }
  }

  /**
   * 🆕 NEW: Generate pseudo-words (fake words) when real English words are impossible
   */
  private generatePseudoWordSequences(maxComplexity: number, activeFingers: FingerType[]): Letter[][] {
    console.log('🔄 Generating pseudo-words for restricted finger combination:', activeFingers);
    
    // Get valid keys, excluding space
    const validKeys = getKeysForFingers(activeFingers).filter(k => k !== 'SPACE' && k !== '_');
    
    if (validKeys.length === 0) {
      console.warn('⚠️ No valid keys for pseudo-words, falling back to basic letter generation');
      return super.generateLetterSequences(maxComplexity);
    }

    const pseudoPhrases: string[] = [];
    const SESSION_DURATION_MS = 120000;
    const ESTIMATED_TIME_PER_SEQUENCE = 3000;
    const TOTAL_SEQUENCES_NEEDED = Math.ceil(SESSION_DURATION_MS / ESTIMATED_TIME_PER_SEQUENCE);

    // Generate ~40 pseudo-phrases
    for (let i = 0; i < TOTAL_SEQUENCES_NEEDED; i++) {
      const phraseLength = Math.floor(Math.random() * 3) + 2; // 2 to 4 fake words per phrase
      const words: string[] = [];
      
      for (let w = 0; w < phraseLength; w++) {
        const wordLength = Math.floor(Math.random() * 4) + 3; // 3 to 6 letters per fake word
        let word = '';
        for (let c = 0; c < wordLength; c++) {
          const randomChar = validKeys[Math.floor(Math.random() * validKeys.length)];
          // Only take first character of the key (to avoid inserting things like "SHIFT")
          if (randomChar.length === 1) {
            word += randomChar.toLowerCase();
          } else {
            word += validKeys[Math.floor(Math.random() * validKeys.length)].charAt(0).toLowerCase();
          }
        }
        words.push(word);
      }
      
      pseudoPhrases.push(words.join(' '));
    }

    // Convert the strings to Letter sequences
    const letterSequences: Letter[][] = [];
    let globalIndex = 0;

    pseudoPhrases.forEach((phrase) => {
      const letters: Letter[] = [];
      const words = phrase.split(' ');

      words.forEach((word, wordIdx) => {
        if (word.trim()) {
          // Add word letters
          for (let i = 0; i < word.length; i++) {
            const char = word[i];
            const finger = getFingerForKey(char.toUpperCase());

            if (finger) {
              letters.push({
                id: `pseudo-${globalIndex}`,
                char: char,
                status: 'neutral',
                expectedFinger: finger,
                timestamp: Date.now()
              });
              globalIndex++;
            }
          }

          // Add space after word (except last word)
          if (wordIdx < words.length - 1) {
            const spaceFinger = getFingerForKey('SPACE');
            if (spaceFinger) {
              letters.push({
                id: `pseudo-space-${globalIndex}`,
                char: '_',
                status: 'neutral',
                expectedFinger: spaceFinger,
                timestamp: Date.now()
              });
              globalIndex++;
            }
          }
        }
      });

      // Split the phrase into chunks matching maxComplexity
      for (let i = 0; i < letters.length; i += maxComplexity) {
        letterSequences.push(letters.slice(i, i + maxComplexity));
      }
    });

    return letterSequences;
  }

  /**
   * 🆕 NEW: Generate sequences from manual word selection with repetition logic
   */
  private async generateManualWordSequences(
    maxComplexity: number,
    manualSelection: {
      selectedWords: string[];
      selectedPhrases: string[];
      randomizeOrder: boolean;
    }
  ): Promise<Letter[][]> {
    console.log('🔁 [WORD REPETITION] Generating manual word sequences:', {
      wordsCount: manualSelection.selectedWords.length,
      phrasesCount: manualSelection.selectedPhrases.length,
      randomizeOrder: manualSelection.randomizeOrder
    });

    const allItems = [...manualSelection.selectedWords, ...manualSelection.selectedPhrases];

    if (allItems.length === 0) {
      console.warn('⚠️ [WORD REPETITION] No items selected - falling back to letter generation');
      return super.generateLetterSequences(maxComplexity);
    }

    // 🕰️ Calculate time distribution for 2-minute session
    const SESSION_DURATION_MS = 120000; // 2 minutes
    const ESTIMATED_TIME_PER_SEQUENCE = 3000; // 3 seconds per sequence (conservative)
    const TOTAL_SEQUENCES_NEEDED = Math.ceil(SESSION_DURATION_MS / ESTIMATED_TIME_PER_SEQUENCE);

    console.log('📈 [WORD REPETITION] Session planning:', {
      sessionDuration: SESSION_DURATION_MS,
      timePerSequence: ESTIMATED_TIME_PER_SEQUENCE,
      totalSequencesNeeded: TOTAL_SEQUENCES_NEEDED,
      itemsToDistribute: allItems.length
    });

    let sequenceDistribution: string[];

    if (manualSelection.randomizeOrder) {
      // 🎲 Random distribution: repeat items randomly throughout session
      console.log('🎲 [WORD REPETITION] Using random distribution');
      sequenceDistribution = this.createRandomDistribution(allItems, TOTAL_SEQUENCES_NEEDED);
    } else {
      // ⏱️ Sequential distribution: each item gets equal time
      console.log('⏱️ [WORD REPETITION] Using sequential distribution');
      sequenceDistribution = this.createSequentialDistribution(allItems, TOTAL_SEQUENCES_NEEDED);
    }

    console.log('🗺️ [WORD REPETITION] Distribution plan:', {
      totalDistributed: sequenceDistribution.length,
      uniqueItems: new Set(sequenceDistribution).size,
      firstFew: sequenceDistribution.slice(0, 10),
      lastFew: sequenceDistribution.slice(-5)
    });

    // Convert distribution to letter sequences
    const letterSequences: Letter[][] = [];
    let globalIndex = 0;

    sequenceDistribution.forEach((item) => {
      const letters: Letter[] = [];

      // Handle phrases (contains spaces) vs single words
      if (item.includes(' ')) {
        // Process phrase: split into words but keep as one sequence
        const words = item.split(' ');
        words.forEach((word, wordIdx) => {
          if (word.trim()) {
            // Add word letters
            for (let i = 0; i < word.length; i++) {
              const char = word[i];
              const finger = getFingerForKey(char.toUpperCase());

              if (finger) {
                letters.push({
                  id: `manual-${globalIndex}`,
                  char: char,
                  status: 'neutral',
                  expectedFinger: finger,
                  timestamp: Date.now()
                });
                globalIndex++;
              }
            }

            // Add space after word (except last word)
            if (wordIdx < words.length - 1) {
              const spaceFinger = getFingerForKey('SPACE');
              if (spaceFinger) {
                letters.push({
                  id: `manual-space-${globalIndex}`,
                  char: '_',
                  status: 'neutral',
                  expectedFinger: spaceFinger,
                  timestamp: Date.now()
                });
                globalIndex++;
              }
            }
          }
        });
      } else {
        // Process single word
        for (let i = 0; i < item.length; i++) {
          const char = item[i];
          const finger = getFingerForKey(char.toUpperCase());

          if (finger) {
            letters.push({
              id: `manual-${globalIndex}`,
              char: char,
              status: 'neutral',
              expectedFinger: finger,
              timestamp: Date.now()
            });
            globalIndex++;
          }
        }
      }

      if (letters.length > 0) {
        letterSequences.push(letters);
      }
    });

    console.log(`✅ [WORD REPETITION] Generated ${letterSequences.length} sequences from manual selection`);
    console.log('🔍 [WORD REPETITION] First few sequences:');
    letterSequences.slice(0, 5).forEach((seq, i) => {
      console.log(`  Sequence ${i + 1}: "${seq.map(l => l.char).join('')}" (${seq.length} chars)`);
    });

    return letterSequences;
  }

  /**
   * 🎲 Create random distribution of items across session
   */
  private createRandomDistribution(items: string[], totalSequences: number): string[] {
    const distribution: string[] = [];

    for (let i = 0; i < totalSequences; i++) {
      const randomItem = items[Math.floor(Math.random() * items.length)];
      distribution.push(randomItem);
    }

    return distribution;
  }

  /**
   * ⏱️ Create sequential distribution - each item gets equal time
   */
  private createSequentialDistribution(items: string[], totalSequences: number): string[] {
    const distribution: string[] = [];
    // Distribute sequences evenly by cycling through items
    for (let i = 0; i < totalSequences; i++) {
      distribution.push(items[i % items.length]);
    }

    return distribution;
  }

  /**
   * 🆕 NEW: Generate more sequences with manual selection (for sequence extension)
   */
  public async generateMoreSequencesWithManualSelection(
    maxComplexity: number,
    additionalCount: number,
    manualSelection: any
  ): Promise<Letter[][]> {
    console.log(`🔁 [WORD REPETITION] Generating ${additionalCount} additional sequences with manual selection`);

    if (manualSelection?.mode === 'manual' && manualSelection.manual) {
      return await this.generateManualWordSequences(maxComplexity, manualSelection.manual);
    }

    // Fallback to standard generation
    console.log('⚠️ [WORD REPETITION] No manual selection - falling back to standard generation');
    return await this.generateMoreSequences(maxComplexity, additionalCount);
  }

  /**
   * Converts word training content to letter sequences - FIXED: No word breaking in word mode
   */
  private convertWordsToLetterSequences(
    wordContent: WordTrainingContent,
    maxComplexity: number
  ): Letter[][] {
    console.log('🔄 [INVESTIGATION] Converting word content to letter sequences (NO WORD BREAKING in word mode)');
    console.log('🔍 [INVESTIGATION] Word content details:', {
      wordsCount: wordContent.words.length,
      phrasesCount: wordContent.phrases.length,
      fingerCombination: wordContent.fingerCombination,
      hand: wordContent.hand
    });
    console.log('🔍 [INVESTIGATION] maxComplexity parameter:', maxComplexity);
    console.log('🔍 [INVESTIGATION] CRITICAL: maxComplexity should NOT affect word integrity - words should stay complete');

    const letterSequences: Letter[][] = [];
    let globalIndex = 0;

    // Process phrases as complete units (NO CHUNKING)
    console.log('🔍 [INVESTIGATION] Processing phrases as complete units...');
    wordContent.phrases.forEach((phrase, phraseIndex) => {
      console.log(`🔍 [INVESTIGATION] Processing phrase ${phraseIndex + 1}: "${phrase}"`);

      // Split phrase into words, but keep each word intact
      const words = phrase.split(' ');
      console.log(`🔍 [INVESTIGATION] Phrase split into ${words.length} words:`, words);
      words.forEach((word, wordIdx) => {
        if (word.trim()) {
          const letters: Letter[] = [];

          // Add the word letters
          for (let i = 0; i < word.length; i++) {
            const char = word[i];
            const finger = getFingerForKey(char.toUpperCase());

            if (finger) {
              letters.push({
                id: `word-${globalIndex}`,
                char: char,
                status: 'neutral',
                expectedFinger: finger,
                timestamp: Date.now()
              });
              globalIndex++;
            } else {
              console.warn(`⚠️ No finger mapping found for character: "${char}"`);
            }
          }

          // Add space after word (except for last word)
          if (wordIdx < words.length - 1) {
            const spaceFinger = getFingerForKey('SPACE');
            if (spaceFinger) {
              letters.push({
                id: `space-${globalIndex}`,
                char: '_', // Use underscore for space visualization
                status: 'neutral',
                expectedFinger: spaceFinger,
                timestamp: Date.now()
              });
              globalIndex++;
            }
          }

          if (letters.length > 0) {
            letterSequences.push(letters);
            console.log(`🔍 [INVESTIGATION] Added complete word/phrase unit: "${letters.map(l => l.char).join('')}" (${letters.length} letters)`);
            console.log(`🔍 [INVESTIGATION] This word will be typed as ONE COMPLETE SEQUENCE - no breaking!`);
          }
        }
      });
    });

    console.log(`🔍 After processing phrases: ${letterSequences.length} sequences`);

    // Process individual words as complete units
    if (letterSequences.length < 20) {
      console.log('🔍 Adding individual words as complete units...');

      wordContent.words.forEach((word, wordIndex) => {
        if (letterSequences.length >= 50) return;

        console.log(`🔍 Processing word ${wordIndex + 1}: "${word}"`);
        const letters: Letter[] = [];

        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const finger = getFingerForKey(char.toUpperCase());

          if (finger) {
            letters.push({
              id: `word-${globalIndex}`,
              char: char,
              status: 'neutral',
              expectedFinger: finger,
              timestamp: Date.now()
            });
            globalIndex++;
          } else {
            console.warn(`⚠️ No finger mapping found for character: "${char}"`);
          }
        }

        if (letters.length > 0) {
          letterSequences.push(letters);
          console.log(`🔍 Added complete word: "${letters.map(l => l.char).join('')}"`);
        }
      });
    }

    console.log(`✅ [INVESTIGATION] Final result: Generated ${letterSequences.length} complete word-based sequences from ${wordContent.phrases.length} phrases and ${wordContent.words.length} words`);

    if (letterSequences.length > 0) {
      console.log('🔍 [INVESTIGATION] First few complete sequences (should be COMPLETE WORDS):');
      letterSequences.slice(0, 3).forEach((seq, i) => {
        console.log(`  [INVESTIGATION] Sequence ${i + 1}: "${seq.map(l => l.char).join('')}" (${seq.length} chars) - THIS IS ONE COMPLETE UNIT`);
      });
    } else {
      console.error('❌ [INVESTIGATION] NO SEQUENCES GENERATED! This is the problem!');
    }

    return letterSequences;
  }

  /**
   * 🔧 ENHANCED: Buffered sequence generation with async word support
   */
  public generateMoreSequences(maxComplexity: number, additionalCount: number = 20): Letter[][] {
    console.log(`🔄 EnhancedTrainingGenerator: Generating ${additionalCount} additional sequences`);
    console.log(`🔄 Buffer status: ${this.sequenceBuffer.length} sequences, maxComplexity: ${maxComplexity}`);
    console.log(`🔄 Available keys: ${this.getAvailableKeys()}`);
    console.log(`🔄 Training mode: ${this.trainingMode.complexity}`);

    try {
      // For word mode, fall back to letter generation for now
      if (this.trainingMode.complexity === 'words') {
        console.log('🗺️ Word mode detected, using letter generation fallback');
        return super.generateMoreSequences(maxComplexity, additionalCount);
      }

      // Use parent synchronous generation for letter modes
      const newSequences = super.generateMoreSequences(maxComplexity, additionalCount);

      if (newSequences.length === 0) {
        console.error('❌ No sequences generated by parent class!');
        return this.generateFallbackSequences(maxComplexity, additionalCount);
      }

      console.log(`✅ Generated ${newSequences.length} fresh sequences`);
      return newSequences;
    } catch (error) {
      console.error('❌ Error in enhanced sequence generation:', error);
      return this.generateFallbackSequences(maxComplexity, additionalCount);
    }
  }



  /**
   * 🔧 NEW: Fallback sequence generation to prevent single-letter loops
   */
  private generateFallbackSequences(maxComplexity: number, additionalCount: number): Letter[][] {
    console.log('🚨 Using fallback sequence generation');
    const fallbackSequences: Letter[][] = [];
    const timestamp = performance.now();

    try {
      const availableKeys = this.getAvailableKeys();
      if (availableKeys.length === 0) {
        console.error('❌ No available keys for fallback generation!');
        return [];
      }

      console.log(`🔧 Fallback using keys: ${availableKeys}`);

      for (let i = 0; i < additionalCount; i++) {
        // Generate sequences with proper complexity (not just single letters)
        const sequenceLength = Math.max(1, Math.min(maxComplexity, this.getFingerConfig().letterComplexity.max));
        const letters: Letter[] = [];

        for (let j = 0; j < sequenceLength; j++) {
          const randomKey = availableKeys[Math.floor(Math.random() * availableKeys.length)];
          const finger = getFingerForKey(randomKey === '_' ? 'SPACE' : randomKey);

          letters.push({
            id: `fallback-${timestamp}-${i}-${j}`,
            char: randomKey,
            status: 'neutral',
            expectedFinger: finger || 'leftPointing', // Safe fallback
            timestamp: Date.now()
          });
        }

        fallbackSequences.push(letters);
      }

      console.log(`✅ Generated ${fallbackSequences.length} fallback sequences`);
      return fallbackSequences;
    } catch (error) {
      console.error('❌ Even fallback generation failed:', error);
      return [];
    }
  }

  /**
   * 🔧 NEW: Get available keys for debugging
   */
  private getAvailableKeys(): string[] {
    const activeFingers = getAllActiveFingers(this.getFingerConfig());
    const keys = getKeysForFingers(activeFingers);
    return keys.map(key => key === 'SPACE' ? '_' : key);
  }

  /**
   * 🔧 PERFORMANCE: Background buffer filling
   */
  private fillBuffer(): void {
    if (this.generationInProgress || this.sequenceBuffer.length >= this.MAX_BUFFER_SIZE) {
      return;
    }

    this.generationInProgress = true;

    try {
      const sequencesToGenerate = this.BUFFER_SIZE - this.sequenceBuffer.length;
      const maxComplexity = this.getFingerConfig().letterComplexity.max;
      const newSequences = super.generateMoreSequences(maxComplexity, sequencesToGenerate);

      this.sequenceBuffer.push(...newSequences);
      console.log(`🔋 Buffer filled: ${this.sequenceBuffer.length} sequences available`);
    } catch (error) {
      console.error('❌ Error filling sequence buffer:', error);
    } finally {
      this.generationInProgress = false;
    }
  }

  /**
   * 🔧 MEMORY: Clean up buffer when no longer needed
   */
  public cleanup(): void {
    this.sequenceBuffer = [];
    this.generationInProgress = false;
    console.log('🧹 EnhancedTrainingGenerator buffer cleaned up');
  }

  /**
   * Get training mode information
   */
  public getTrainingMode(): TrainingMode {
    return this.trainingMode;
  }
}

/**
 * Factory function to create a training generator for a specific day
 */
export const createTrainingGenerator = (fingerConfig: FingerConfig): TrainingGenerator => {
  return new TrainingGenerator(fingerConfig);
};

/**
 * 🔧 PHASE 1 FIX: Factory function with memory management
 */
export const createEnhancedTrainingGenerator = (fingerConfig: FingerConfig, trainingMode: TrainingMode): EnhancedTrainingGenerator => {
  const generator = new EnhancedTrainingGenerator(fingerConfig, trainingMode);

  // 🔧 MEMORY: Register cleanup for when generator is no longer needed
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      generator.cleanup();
    });
  }

  return generator;
};

// Export the class for type checking
// Note: EnhancedTrainingGenerator is already exported above

/**
 * Utility function to validate if a training configuration is valid
 */
export const validateFingerConfig = (config: FingerConfig): boolean => {
  const activeFingers = getAllActiveFingers(config);

  if (activeFingers.length === 0) {
    console.warn('No active fingers in configuration:', config);
    return false;
  }

  const availableKeys = getKeysForFingers(activeFingers);
  if (availableKeys.length === 0) {
    console.warn('No available keys for active fingers:', activeFingers);
    return false;
  }

  if (config.letterComplexity.min > config.letterComplexity.max) {
    console.warn('Invalid complexity range:', config.letterComplexity);
    return false;
  }

  return true;
};